
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Key, Server, Lock, Shield, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import RoleGuard from '../../components/RoleGuard';

interface AIConfig {
    id: string;
    org_id: string | null;
    provider: string; // openai, gemini, anthropic, bedrock
    is_active: boolean;
    is_default: boolean;
    created_at: string;
}

const AIGateway: React.FC = () => {
    const [configs, setConfigs] = useState<AIConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);

    // Form State
    const [provider, setProvider] = useState('openai');
    const [apiKey, setApiKey] = useState('');
    const [awsAccessKey, setAwsAccessKey] = useState('');
    const [awsSecretKey, setAwsSecretKey] = useState('');
    const [region, setRegion] = useState('us-east-1');
    const [isDefault, setIsDefault] = useState(true); // Default to true for global
    const [targetOrgId, setTargetOrgId] = useState(''); // Optional: for specific tenant override

    const [verifying, setVerifying] = useState(false);
    const [verificationResult, setVerificationResult] = useState<{ success: boolean; message: string; details?: string } | null>(null);

    useEffect(() => {
        loadConfigs();
    }, []);

    const loadConfigs = async () => {
        setLoading(true);
        try {
            const data = await api.getAiGatewayConfigs();
            setConfigs(data);
        } catch (e) {
            console.error("Error loading configs", e);
        } finally {
            setLoading(false);
        }
    };

    const getPayload = () => {
        const payload: any = {
            provider,
            is_default: isDefault,
            org_id: targetOrgId || null
        };

        if (provider === 'bedrock') {
            payload.aws_access_key_id = awsAccessKey;
            payload.aws_secret_access_key = awsSecretKey;
            payload.region_name = region;
        } else {
            payload.api_key = apiKey;
        }
        return payload;
    };

    const handleVerify = async () => {
        setVerifying(true);
        setVerificationResult(null);
        try {
            const payload = getPayload();
            const result = await api.verifyAiGatewayConfig(payload);
            setVerificationResult(result);
        } catch (e: any) {
            setVerificationResult({
                success: false,
                message: e.response?.data?.detail || e.message || "Error de conexión"
            });
        } finally {
            setVerifying(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const payload = getPayload();
            await api.createAiGatewayConfig(payload);
            setIsCreating(false);
            resetForm();
            loadConfigs();
        } catch (e) {
            alert("Error creating config");
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("¿Seguro que deseas eliminar esta configuración?")) return;
        try {
            await api.deleteAiGatewayConfig(id);
            loadConfigs();
        } catch (e) {
            console.error(e);
        }
    };

    const resetForm = () => {
        setApiKey('');
        setAwsAccessKey('');
        setAwsSecretKey('');
        setRegion('us-east-1');
        setProvider('openai');
        setVerificationResult(null);
    };

    const ProviderIcon = ({ p }: { p: string }) => {
        switch (p) {
            case 'openai': return <div className="w-8 h-8 rounded bg-green-500/20 text-green-500 flex items-center justify-center font-bold">O</div>
            case 'gemini': return <div className="w-8 h-8 rounded bg-blue-500/20 text-blue-500 flex items-center justify-center font-bold">G</div>
            case 'anthropic': return <div className="w-8 h-8 rounded bg-orange-500/20 text-orange-500 flex items-center justify-center font-bold">A</div>
            case 'bedrock': return <div className="w-8 h-8 rounded bg-yellow-500/20 text-yellow-500 flex items-center justify-center font-bold">B</div>
            default: return <Server className="w-5 h-5" />;
        }
    }

    return (
        <RoleGuard allowed={['admin']}>
            <div className="max-w-6xl mx-auto space-y-8">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Shield className="w-6 h-6 text-primary" />
                            AI Gateway
                        </h1>
                        <p className="text-slate-400">Gestión centralizada de credenciales y proveedores de IA.</p>
                    </div>
                    <button
                        onClick={() => setIsCreating(true)}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Nueva Configuración
                    </button>
                </div>

                {/* List */}
                <div className="bg-surface-dark border border-slate-800 rounded-xl overflow-hidden">
                    <div className="p-4 border-b border-slate-800 bg-slate-900/50 flex justify-between items-center">
                        <h3 className="font-medium text-white">Configuraciones Activas</h3>
                        <span className="text-xs text-slate-500">{configs.length} items</span>
                    </div>

                    {loading ? (
                        <div className="p-8 text-center text-slate-500">Cargando...</div>
                    ) : configs.length === 0 ? (
                        <div className="p-12 text-center text-slate-500">
                            <Key className="w-12 h-12 mx-auto mb-4 opacity-20" />
                            <p>No hay configuraciones activas.</p>
                            <p className="text-sm">Agrega una llave de OpenAI o Bedrock para comenzar.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-800">
                            {configs.map(cfg => (
                                <div key={cfg.id} className="p-4 flex items-center justify-between hover:bg-slate-800/30 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <ProviderIcon p={cfg.provider} />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-medium text-white capitalize">{cfg.provider}</h4>
                                                {cfg.org_id ? (
                                                    <span className="px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 text-[10px]">Tenant: {cfg.org_id}</span>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-[10px] border border-purple-500/30">GLOBAL SYSTEM</span>
                                                )}
                                                {cfg.is_default && <CheckCircle className="w-3 h-3 text-green-500" />}
                                            </div>
                                            <p className="text-xs text-slate-500 font-mono mt-1">
                                                ID: {cfg.id.substring(0, 8)}... • Created: {new Date(cfg.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1 text-slate-500 bg-slate-900 px-3 py-1 rounded text-xs">
                                            <Lock className="w-3 h-3" />
                                            <span>Encrypted</span>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(cfg.id)}
                                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                            title="Revocar Llave"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modal / Form */}
                {isCreating && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                        <div className="bg-surface-dark border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
                            <div className="p-6 border-b border-slate-800">
                                <h3 className="text-xl font-bold text-white">Nueva Configuración IA</h3>
                            </div>
                            <form onSubmit={handleCreate} className="p-6 space-y-4">

                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-1">Proveedor</label>
                                    <select
                                        value={provider}
                                        onChange={e => setProvider(e.target.value)}
                                        className="w-full bg-slate-800 border-slate-700 rounded-lg px-3 py-2 text-white"
                                    >
                                        <option value="openai">OpenAI</option>
                                        <option value="anthropic">Anthropic (Claude)</option>
                                        <option value="gemini">Google Gemini</option>
                                        <option value="bedrock">AWS Bedrock</option>
                                    </select>
                                </div>

                                {provider === 'bedrock' ? (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-1">AWS Access Key ID</label>
                                            <input
                                                type="text"
                                                required
                                                value={awsAccessKey}
                                                onChange={e => setAwsAccessKey(e.target.value)}
                                                className="w-full bg-slate-800 border-slate-700 rounded-lg px-3 py-2 text-white font-mono"
                                                placeholder="AKIA..."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-1">AWS Secret Access Key</label>
                                            <input
                                                type="password"
                                                required
                                                value={awsSecretKey}
                                                onChange={e => setAwsSecretKey(e.target.value)}
                                                className="w-full bg-slate-800 border-slate-700 rounded-lg px-3 py-2 text-white font-mono"
                                                placeholder="Secret..."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-1">AWS Region</label>
                                            <input
                                                type="text"
                                                required
                                                value={region}
                                                onChange={e => setRegion(e.target.value)}
                                                className="w-full bg-slate-800 border-slate-700 rounded-lg px-3 py-2 text-white"
                                                placeholder="us-east-1"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-1">API Key</label>
                                        <input
                                            type="password"
                                            required
                                            value={apiKey}
                                            onChange={e => setApiKey(e.target.value)}
                                            className="w-full bg-slate-800 border-slate-700 rounded-lg px-3 py-2 text-white font-mono"
                                            placeholder="sk-..."
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-1">Tenant ID (Opcional)</label>
                                    <input
                                        type="text"
                                        value={targetOrgId}
                                        onChange={e => setTargetOrgId(e.target.value)}
                                        className="w-full bg-slate-800 border-slate-700 rounded-lg px-3 py-2 text-white"
                                        placeholder="Dejar vacío para Global (System Wide)"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">Si especificas un ID de organización, esta llave solo funcionará para ellos.</p>
                                </div>

                                <div className="flex items-center gap-2 mt-4">
                                    <input
                                        type="checkbox"
                                        id="isDefault"
                                        checked={isDefault}
                                        onChange={e => setIsDefault(e.target.checked)}
                                        className="rounded border-slate-600 bg-slate-700"
                                    />
                                    <label htmlFor="isDefault" className="text-sm text-slate-300">Set as Default for context</label>
                                </div>

                                {/* Verification Result */}
                                {verificationResult && (
                                    <div className={`mt-4 p-3 rounded-lg text-sm border ${verificationResult.success
                                            ? 'bg-green-500/10 border-green-500/30 text-green-400'
                                            : 'bg-red-500/10 border-red-500/30 text-red-400'
                                        }`}>
                                        <div className="font-bold flex items-center gap-2">
                                            {verificationResult.success ? <CheckCircle className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                                            {verificationResult.message}
                                        </div>
                                        {verificationResult.details && (
                                            <div className="mt-1 opacity-80 font-mono text-xs">
                                                {verificationResult.details}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="flex justify-between items-center mt-6 pt-4 border-t border-slate-800">
                                    <div>
                                        <button
                                            type="button"
                                            onClick={handleVerify}
                                            disabled={verifying}
                                            className={`text-sm px-3 py-1.5 rounded flex items-center gap-2 transition-colors ${verifying
                                                    ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                                                    : 'bg-slate-800 text-indigo-400 hover:bg-slate-700 border border-slate-700'
                                                }`}
                                        >
                                            {verifying ? (
                                                <>
                                                    <div className="w-3 h-3 border-2 border-slate-500 border-t-white rounded-full animate-spin"></div>
                                                    Verificando...
                                                </>
                                            ) : (
                                                <>
                                                    <Shield className="w-3 h-3" />
                                                    Verificar Conexión
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsCreating(false)}
                                            className="px-4 py-2 text-slate-400 hover:text-white"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600"
                                        >
                                            Guardar Credenciales
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </RoleGuard>
    );
};

export default AIGateway;
